import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IStorefrontSettings, buildStorefrontPatch } from '../../../../models/Storefront';
import {
    useGetGlobalStorefrontSettingsQuery,
    useGetStorefrontQuery,
    usePatchStorefrontMutation,
} from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { MessageBar, MessageBarType, Stack, StackItem, TextField, Toggle } from '@fluentui/react';
import ImageEditor from '../../../../components/Inputs/ImageEditor';
import StorefrontDomainDropdown from '../../../../components/Storefront/StorefrontDomainDropdown';
import CustomDomainsSection from '../../../../components/Storefront/CustomDomainsSection';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import { toast } from 'react-toastify';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import { AssetSourceProvider } from '../../../../components/Storefront/Assets/AssetSourceContext';
import useStorefrontEditLease from '../../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../../logic/storefrontLeaseConflict';
import Setting from '../../../../components/Common/Setting';

interface StorefrontGeneralSettingsPageParams {
    storefront: string;
}

const DEBOUNCE_MS = 3000;

const StorefrontGeneralSettingsPage: React.FC<RouteComponentProps<StorefrontGeneralSettingsPageParams>> = (props) => {
    const { storefront } = props.match.params;
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const [patchStorefront] = usePatchStorefrontMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IStorefrontSettings | undefined>(undefined);
    const localSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const globalSettings = useGetGlobalStorefrontSettingsQuery();
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const [displayDomain, setDisplayDomain] = React.useState<string>();
    // Every field here autosaves, so a lease held by an assistant turn would turn each edit into a
    // rejected patch. One flag switches the whole form off instead.
    const { locked: readOnly } = useStorefrontEditLease(storefront);

    const setLocalSettings = React.useCallback((s: IStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setLocalSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data, setLocalSettings]);

    const handleSettingsChanged = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    React.useEffect(() => {
        if (localSettings?.domain) {
            setDisplayDomain(localSettings.domain);
        } else {
            setDisplayDomain('store.mspcart.com');
        }
    }, [localSettings?.domain]);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildStorefrontPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchStorefront({ id: storefront, patch });
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        if (!current.subUrl?.trim()) return;
        const patch = buildStorefrontPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchStorefront({ id: storefront, patch }).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
        }
    }, [patchStorefront, storefront]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    const saveNow = React.useCallback(() => {
        if (debounceSaveRef.current) {
            clearTimeout(debounceSaveRef.current);
            debounceSaveRef.current = null;
        }
        if (localSettingsRef.current) doSave(localSettingsRef.current);
    }, [doSave]);

    if (storefrontQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const handleTextChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        const name = event.currentTarget.getAttribute('aria-field');
        if (!name || !localSettingsRef.current) return;
        const updated = { ...localSettingsRef.current, [name]: newValue ?? '' };
        setLocalSettings(updated);
        scheduleSave();
    };

    const handleToggleChange = (event: React.FormEvent<HTMLElement>, checked?: boolean) => {
        const name = event.currentTarget.getAttribute('aria-field');
        if (!name || !localSettingsRef.current) return;
        const updated = { ...localSettingsRef.current, [name]: checked ?? false };
        setLocalSettings(updated);
        doSave(updated);
    };

    const handleDomainChange = (domain: string) => {
        if (!localSettingsRef.current) return;
        const updated = { ...localSettingsRef.current, domain };
        setLocalSettings(updated);
        doSave(updated);
    };

    return (
        <AssetSourceProvider value={{ storefrontId: storefront }}>
        <div>
            <StorefrontSettingsTitleBar
                storefront={storefront}
                title="General Settings"
                onSettingsChanged={handleSettingsChanged}
            />
            <StorefrontReadOnlyRegion readOnly={readOnly}>
            <Stack tokens={{ childrenGap: '1em' }}>
                <Setting id='storefront.general.name'>
                    <TextField
                        label='Storefront Name'
                        value={localSettings.name ?? ''}
                        aria-field='name'
                        onChange={handleTextChange}
                        onBlur={saveNow}
                    />
                </Setting>
                <Setting id='storefront.general.domain'>
                    <StorefrontDomainDropdown
                        label='Storefront Domain'
                        onChange={handleDomainChange}
                        value={localSettings.domain ?? ''}
                    />
                </Setting>
                <Setting id='storefront.general.customDomains'>
                    <CustomDomainsSection />
                </Setting>
                <StackItem>
                    <Setting id='storefront.general.subUrl'>
                        <TextField
                            label='Sub URL'
                            value={localSettings.subUrl ?? ''}
                            aria-field='subUrl'
                            prefix={`https://${displayDomain}/${organization}/`}
                            onChange={handleTextChange}
                            onBlur={saveNow}
                        />
                    </Setting>
                    {localSettings.subUrl !== storefrontQuery.data?.subUrl && (
                        <MessageBar messageBarType={MessageBarType.severeWarning}>
                            Changing the sub URL will change the URL for this storefront. Any emailed links will no longer work and any links to this storefront will need to be updated.
                        </MessageBar>
                    )}
                </StackItem>
                <Setting id='storefront.general.favicon'>
                    <ImageEditor
                        label='Favicon'
                        value={localSettings.favicon}
                        onChange={(v) => {
                            const updated = { ...localSettingsRef.current!, favicon: v };
                            setLocalSettings(updated);
                            doSave(updated);
                        }}
                        height={96}
                        imageId={storefront}
                        onClear={() => {
                            const updated = { ...localSettingsRef.current!, favicon: '' };
                            setLocalSettings(updated);
                            doSave(updated);
                        }}
                        imageType='Storefront'
                    />
                </Setting>
                <Setting id='storefront.general.enabled'>
                    <Toggle
                        label='Storefront Active'
                        checked={localSettings.enabled ?? false}
                        aria-field='enabled'
                        onChange={handleToggleChange}
                    />
                </Setting>
            </Stack>
            </StorefrontReadOnlyRegion>
        </div>
        </AssetSourceProvider>
    );
};

export default StorefrontGeneralSettingsPage;